<template>
  <div :class="$style.box">
    <UserThumbnail
      :class="[$style.box__thumbnail, $style[`box__thumbnail--${size}`]]"
      :personalityType="personalityType"
      v-if="!noThumbnail"
    />
    <div :class="[$style.box__profile, $style.profile]">
      <p
        :class="[
          $style.profile__character,
          $style.character,
          $style[`character--${size}`],
        ]"
      >
        <span v-if="$slots.characterChip" :class="$style.character__chip">
          <slot name="characterChip" />
        </span>
        <span
          :class="[
            $style.character__brief,
            $style[`character__brief--${size}`],
          ]"
        >
          {{ brief }}
        </span>
      </p>
      <p
        :class="[
          $style.profile__nickname,
          $style[`profile__nickname--${size}`],
        ]"
      >
        {{ nickname }}
      </p>
      <div
        :class="[
          $style.profile__additional,
          $style[`profile__additional--${size}`],
        ]"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent, computed } from 'vue'
import UserThumbnail from '@/components/atoms/UserThumbnail.vue'

import type { PersonalityType } from '@/types/app'

type Size = 'large' | 'middle' | 'small'

export default defineComponent({
  name: 'UserProfileBox',
  components: {
    UserThumbnail,
  },
  props: {
    personalityType: {
      type: String as PropType<Maybe<PersonalityType>>,
      default: undefined,
    },
    name: {
      type: String,
      default: '',
    },
    brief: {
      type: String,
      required: true,
    },
    size: {
      type: String as PropType<Size>,
      default: 'middle',
    },
    noThumbnail: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const nickname = computed(() => (props.name !== '' ? props.name : 'ゲスト'))

    return {
      nickname,
    }
  },
})
</script>

<style lang="scss" module>
.box {
  display: flex;
  align-items: center;

  &__thumbnail {
    flex: 0 0 auto;

    &--small {
      width: 40px;
      height: 40px;
    }

    &--middle {
      width: 56px;
      height: 56px;
    }

    &--large {
      width: 80px;
      height: 80px;
    }
  }

  &__profile {
    flex-grow: 0;

    &:nth-child(n + 2) {
      margin-left: 16px;
    }
  }
}

.profile {
  &__character {
    align-items: center;
    line-height: 1;
  }

  &__nickname {
    color: $color-text-and-object-high-emphasis;
    font-weight: bold;
    line-height: 1.5;

    &--small {
      font-size: 14px;

      &:nth-child(n + 2) {
        margin-top: 4px;
      }
    }

    &--middle {
      font-size: 18px;

      &:nth-child(n + 2) {
        margin-top: 6px;
      }
    }

    &--large {
      font-size: 18px;

      &:nth-child(n + 2) {
        margin-top: 6px;
      }
    }
  }

  &__additional {
    &--small {
      &:nth-child(n + 2) {
        margin-top: 4px;
      }
    }

    &--middle {
      &:nth-child(n + 2) {
        margin-top: 4px;
      }
    }

    &--large {
      &:nth-child(n + 2) {
        margin-top: 4px;
      }
    }
  }
}

.character {
  $root: &;

  display: flex;
  align-items: center;

  &--small {
    line-height: 16px;
  }

  &--middle {
    line-height: 24px;
  }

  &--large {
    line-height: 16px;
  }

  &__chip {
    flex-shrink: 0;
  }

  &__brief {
    overflow: hidden;
    color: $color-text-and-object-medium-emphasis;
    font-size: 12px;
    white-space: nowrap;

    &:nth-child(n + 2) {
      margin-left: 8px;
    }
  }
}
</style>
